'use client';

import { ReactNode, useEffect, useRef, useState } from 'react';
import { useLocale, useMessages, NextIntlClientProvider } from 'next-intl';
import { type Locale } from '@/i18n/config';
import { getLocaleFileName } from '@/i18n/locale-file-mapper';
import { onI18nError, setInitialMessages } from '@/i18n/on-i18n-error';
import logger from '@/utils/client-logger';

/**
 * 深层补充：将 source 中 target 缺失的 key（含嵌套）补充进去，不替换已有值。
 * 无变化时返回 target 原引用，避免触发重渲染。
 *
 * 示例：
 *   target = { goodsDetail: { seo: { title: 'Buy' } } }
 *   source = { goodsDetail: { seo: { title: 'Buy', desc: '...' }, tabs: { ... } } }
 *   → { goodsDetail: { seo: { title: 'Buy', desc: '...' }, tabs: { ... } } }
 */
function deepSupplement<T extends Record<string, any>>(target: T, source: Record<string, any>): T {
  let result: Record<string, any> | null = null;

  for (const key of Object.keys(source)) {
    if (!(key in target)) {
      // 整个 key 缺失，直接补充
      if (!result) result = { ...target };
      result[key] = source[key];
    } else if (
      typeof source[key] === 'object' &&
      source[key] !== null &&
      !Array.isArray(source[key]) &&
      typeof target[key] === 'object' &&
      target[key] !== null &&
      !Array.isArray(target[key])
    ) {
      // 两边都是普通对象，递归补充子 key
      const merged = deepSupplement(target[key], source[key]);
      if (merged !== target[key]) {
        if (!result) result = { ...target };
        result[key] = merged;
      }
    }
    // 已有的非对象值（string/number）不替换
  }

  return (result as T) ?? target;
}

/**
 * I18n 预加载 Provider
 *
 * 在 NextIntlClientProvider 内部包裹此 provider
 * 用于非阻塞地预加载所有语言包
 *
 * 工作原理：
 * 1. 首屏加载只发送初始路由的语言包（快速）
 * 2. 组件挂载时，使用 requestIdleCallback 异步加载完整语言包
 * 3. 加载完成后只补充缺失的顶层 namespace，不替换已有值
 * 4. 已有 namespace 保持原引用，子组件不会因相同内容重渲染
 */
export function I18nPreloadProvider({ children }: { children: ReactNode }) {
  const locale = useLocale();
  const initialMessages = useMessages() as Record<string, any>;
  const [supplementedMessages, setSupplementedMessages] = useState<Record<string, any> | null>(
    null,
  );

  // 用 ref 避免 async 回调中的 stale closure
  const initialRef = useRef(initialMessages);
  initialRef.current = initialMessages;

  // 注册初始 messages 的 namespace 集合，用于后续 useTranslations 的兜底检测
  setInitialMessages(initialMessages);

  useEffect(() => {
    const preloadMessages = async () => {
      try {
        const siteName = process.env.NEXT_PUBLIC_SITE_NAME;
        const localeFileName = getLocaleFileName(locale as Locale, siteName);
        const loaded = (await import(`@/locales/${localeFileName}.json`)).default;

        const merged = deepSupplement(initialRef.current, loaded);
        // 无新增 namespace 时不触发 setState，避免不必要渲染
        if (merged !== initialRef.current) {
          setSupplementedMessages(merged);
        }

        logger.debug(
          `[i18n] Preloaded full messages for locale: ${locale} (file: ${localeFileName})`,
        );
      } catch (error) {
        logger.warn(`[i18n] Failed to preload messages for ${locale}:`, error);
      }
    };

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      requestIdleCallback(() => preloadMessages(), { timeout: 5000 });
    } else {
      setTimeout(preloadMessages, 0);
    }
  }, [locale]);

  const messages = supplementedMessages ?? initialMessages;

  return (
    <NextIntlClientProvider locale={locale} messages={messages} onError={onI18nError}>
      {children}
    </NextIntlClientProvider>
  );
}
